// =============================================================================
// supplier-me-client.http.ts — Adaptateur HTTP RÉEL du SupplierMeClient (S0-ROLE, Option B).
// =============================================================================
// Câble la SPA contre les deux lectures de session fournisseur (whoami + contacts/me). Substitué derrière
// le MÊME DI seam (SupplierMeClientProvider). LECTURE PURE : aucune mutation, donc PAS de CSRF.
//
// SÉCURITÉ (RULE_REF_006) : la session fournisseur (cookie httpOnly `supplier_session`) n'est JAMAIS lue
// ni fabriquée ici — portée par le cookie (credentials:'include'). Le siret ET le contactId viennent de
// la session côté API. AUCUNE donnée métier en localStorage. Le 401 est mappé en kind 'unauthorized'
// (= absence de session) pour piloter le fallback interim admin du bootstrap.

import {
  SupplierMeApiError,
  type SupplierMeApiErrorKind,
  type SupplierMeClient,
  type SupplierMeDto,
  type SupplierWhoamiDto,
} from '@/api/supplier-me-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Mapping STATUS HTTP → kind (401 = pas de session ⇒ fallback interim admin). */
const STATUS_TO_KIND: Readonly<Record<number, SupplierMeApiErrorKind>> = {
  401: 'unauthorized',
};

/**
 * Adaptateur HTTP réel : appels `fetch` contre les lectures de session fournisseur. `baseUrl` par défaut
 * vide (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`).
 */
export class HttpSupplierMeClient implements SupplierMeClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async whoami(): Promise<SupplierWhoamiDto> {
    const response = await fetch(`${this.baseUrl}/supplier/auth/whoami`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierWhoamiDto;
  }

  public async getMe(): Promise<SupplierMeDto> {
    const response = await fetch(`${this.baseUrl}/supplier/contacts/me`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierMeDto;
  }

  private async toApiError(response: Response): Promise<SupplierMeApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new SupplierMeApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
